import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Activity, Moon, Droplet, Scale } from "lucide-react";

// Combine today + history[] into a 7/14/30 day series.
const buildSeries = (state, days = 7) => {
  const combined = [state.today, ...(state.history || [])];
  // sort oldest → newest
  combined.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const cut = combined.slice(-days);
  return cut.map((d) => ({
    date: (d.date || "").slice(5), // MM-DD
    steps: d.steps || 0,
    water_glasses: Math.round((d.water_ml || 0) / 250),
    sleep: d.sleep_hours || 0,
    weight: d.weight || null,
  }));
};

const METRICS = [
  { key: "steps", label: "Steps", Icon: Activity, accent: "#22d3ee", unit: "" },
  { key: "water_glasses", label: "Water (glasses)", Icon: Droplet, accent: "#60a5fa", unit: "" },
  { key: "sleep", label: "Sleep (hrs)", Icon: Moon, accent: "#a78bfa", unit: "hrs" },
  { key: "weight", label: "Weight (kg)", Icon: Scale, accent: "#34d399", unit: "kg" },
];

const RANGES = [
  { key: 7, label: "7d" },
  { key: 14, label: "14d" },
  { key: 30, label: "30d" },
];

const TrendsPanel = ({ state }) => {
  const [range, setRange] = useState(7);
  const [metric, setMetric] = useState("steps");
  const data = useMemo(() => buildSeries(state, range), [state, range]);
  const active = METRICS.find((m) => m.key === metric);
  const values = data.map((d) => d[metric] || 0).filter((v) => v > 0);
  const latest = values.length ? values[values.length - 1] : 0;
  const avg = values.length
    ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10
    : 0;
  const first = values.length ? values[0] : 0;
  const trend = values.length >= 2 ? latest - first : 0;

  return (
    <motion.div
      data-testid="trends-panel"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
            <TrendingUp className="w-3.5 h-3.5" />
            Trends
          </div>
          <h3 className="mt-1 font-display text-xl font-semibold text-white">
            Your last {range} days
          </h3>
        </div>
        <div className="flex gap-1 rounded-full border border-white/10 bg-white/5 p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`trend-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`text-[11px] px-3 py-1 rounded-full transition-colors ${
                range === r.key
                  ? "bg-white text-black"
                  : "text-zinc-300 hover:bg-white/5"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {METRICS.map((m) => (
          <button
            key={m.key}
            data-testid={`trend-metric-${m.key}`}
            onClick={() => setMetric(m.key)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
              metric === m.key
                ? "border-cyan-400 bg-cyan-400/10 text-white"
                : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"
            }`}
          >
            <m.Icon className="w-3.5 h-3.5" style={{ color: m.accent }} />
            {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatChip label="Latest" value={latest} accent={active.accent} unit={active.unit} />
        <StatChip label="Average" value={avg} accent={active.accent} unit={active.unit} />
        <StatChip
          label="Δ vs first day"
          value={(trend > 0 ? "+" : "") + trend}
          accent={trend >= 0 ? "#34d399" : "#f87171"}
          unit={active.unit}
        />
      </div>

      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={active.accent} stopOpacity={0.55} />
                <stop offset="100%" stopColor={active.accent} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="date"
              stroke="rgba(255,255,255,0.35)"
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              stroke="rgba(255,255,255,0.35)"
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={{
                background: "#0A0A14",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 12,
                fontSize: 12,
                color: "#fff",
              }}
              labelStyle={{ color: "rgba(255,255,255,0.7)" }}
            />
            <Area
              type="monotone"
              dataKey={metric}
              stroke={active.accent}
              strokeWidth={2}
              fill="url(#trendFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {values.length < 2 && (
        <div className="mt-3 text-[11px] text-zinc-500">
          Log a few days to unlock meaningful trends. Yesterday&apos;s data is
          archived automatically at midnight.
        </div>
      )}
    </motion.div>
  );
};

const StatChip = ({ label, value, accent, unit }) => (
  <div className="rounded-2xl border border-white/10 bg-black/40 px-3 py-2">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div
      className="mt-1 font-display text-xl font-semibold tabular-nums"
      style={{ color: accent }}
    >
      {value}
      {unit && <span className="text-[10px] text-zinc-500 ml-0.5">{unit}</span>}
    </div>
  </div>
);

export default TrendsPanel;
